import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { assignableUsersApi, ticketApi } from '../lib/api'
import type { Ticket, TicketCategory, TicketChecklistItem, TicketComment, TicketPriority, TicketQueueFilter, TicketStatus } from '../lib/api'
import { gapListFilters } from './HelpKnowledgeGapsPage'
import { useAuth, usePermissions } from '../hooks/useAuth'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { CreateTicketModal } from '../components/tickets/CreateTicketModal'
import { EditTicketModal } from '../components/tickets/EditTicketModal'
import { TicketActivityTimeline } from '../components/tickets/TicketActivityTimeline'
import { DraftHelpArticleButton } from '../components/tickets/DraftHelpArticleButton'
import { TicketHelpArticles } from '../components/tickets/TicketHelpArticles'
import { CardTitle as SectionCardTitle, PageHeader, SurfaceCard } from '../components/ui'

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
}

const CATEGORY_LABELS: Record<TicketCategory, string> = {
  billing: 'Billing',
  technical: 'Technical',
  onboarding: 'Onboarding',
  general: 'General',
}

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
}

function priorityClass(priority: TicketPriority): string {
  switch (priority) {
    case 'urgent':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'high':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'normal':
      return 'border-blue-200 bg-blue-50 text-blue-700'
  }
}

function statusClass(status: TicketStatus): string {
  switch (status) {
    case 'open':
      return 'border-sky-200 bg-sky-50 text-sky-700'
    case 'in_progress':
      return 'border-violet-200 bg-violet-50 text-violet-700'
    case 'resolved':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'closed':
      return 'border-gray-300 bg-gray-100 text-gray-700'
  }
}

function StatusPill({ status }: { status: TicketStatus }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass(status)}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

function PriorityPill({ priority }: { priority: TicketPriority }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${priorityClass(priority)}`}>
      {PRIORITY_LABELS[priority]}
    </span>
  )
}

function formatAge(date: string): string {
  const diffMinutes = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60000))
  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m`
  const hours = Math.floor(diffMinutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

function slaLabel(ticket: Ticket): string {
  if (!ticket.sla_due_at) return 'No SLA'

  const due = new Date(ticket.sla_due_at).getTime()
  const now = Date.now()
  const diffMinutes = Math.round((due - now) / 60000)

  if (diffMinutes < 0) {
    const overdueMinutes = Math.abs(diffMinutes)
    if (overdueMinutes < 60) return `Overdue ${overdueMinutes}m`
    const overdueHours = Math.floor(overdueMinutes / 60)
    if (overdueHours < 24) return `Overdue ${overdueHours}h`
    return `Overdue ${Math.floor(overdueHours / 24)}d`
  }

  if (diffMinutes < 60) return `${diffMinutes}m left`
  const hours = Math.floor(diffMinutes / 60)
  if (hours < 24) return `${hours}h left`
  return `${Math.floor(hours / 24)}d left`
}

function slaClass(ticket: Ticket): string {
  if (!ticket.sla_due_at) return 'border-gray-200 bg-gray-50 text-gray-700'

  const due = new Date(ticket.sla_due_at).getTime()
  const created = new Date(ticket.created_at).getTime()
  const now = Date.now()
  const total = Math.max(1, due - created)
  const remaining = due - now
  const ratio = remaining / total

  if (remaining <= 0 || ratio <= 0.1) return 'border-rose-200 bg-rose-50 text-rose-700'
  if (ratio <= 0.5) return 'border-amber-200 bg-amber-50 text-amber-700'
  return 'border-emerald-200 bg-emerald-50 text-emerald-700'
}

function SlaPill({ ticket }: { ticket: Ticket }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${slaClass(ticket)}`}>
      {slaLabel(ticket)}
    </span>
  )
}

function slaTypeLabel(ticket: Ticket): string {
  switch (ticket.sla_type) {
    case '1_day':
      return '1d'
    case '2_3_days':
      return '3d'
    case '7_days':
      return '7d'
    case 'beginning_of_next_month':
      return 'EOM'
    case 'custom':
      return `Custom: ${ticket.sla_custom_days ?? '?'}d`
    default:
      return '-'
  }
}

function slaTypeClass(ticket: Ticket): string {
  switch (ticket.sla_type) {
    case 'beginning_of_next_month':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'custom':
      return 'border-gray-200 bg-gray-50 text-gray-700'
    default:
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
}

const QUEUE_LABELS: Record<TicketQueueFilter, string> = {
  past_sla: 'Past SLA',
  due_24h: 'Due within 24 hours',
  unassigned_open: 'Open and unassigned',
  stale: 'Stale',
  reopened: 'Reopened since tracking began',
}

function isQueueFilter(value: string): value is TicketQueueFilter {
  return value in QUEUE_LABELS
}

function recordName(ticket: Ticket): string {
  return ticket.merchant?.dba_name
    ?? ticket.lead?.dba_name
    ?? ticket.lead?.legal_name
    ?? 'Unlinked'
}

function assigneeSummary(ticket: Ticket): string {
  const assignees = ticket.assignees ?? []
  if (assignees.length === 0) return 'Unassigned'
  if (assignees.length <= 2) return assignees.map((assignee) => assignee.name).join(', ')
  return `${assignees.slice(0, 2).map((assignee) => assignee.name).join(', ')} +${assignees.length - 2} more`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function TicketsPage() {
  const { id } = useParams()
  return id ? <TicketDetail id={id} /> : <TicketList />
}

function TicketList() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const queueParam = searchParams.get('queue') ?? ''
  const queue = isQueueFilter(queueParam) ? queueParam : undefined
  const urlAssigned = searchParams.get('assigned_to')
  const gapLink = gapListFilters(searchParams)
  const [includeClosed, setIncludeClosed] = useState(gapLink.isGapLink)
  const [priority, setPriority] = useState<TicketPriority | ''>('')
  const [category, setCategory] = useState<TicketCategory | ''>('')
  const [search, setSearch] = useState('')
  const [assignedMode, setAssignedMode] = useState<'me' | 'all' | 'specific' | 'unassigned'>(() => {
    if (urlAssigned === 'unassigned') return 'unassigned'
    if (urlAssigned) return 'specific'
    if (queue || gapLink.isGapLink) return 'all'
    return 'me'
  })
  const [assignedTo, setAssignedTo] = useState(urlAssigned && urlAssigned !== 'unassigned' ? urlAssigned : '')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data: users } = useQuery({
    queryKey: ['assignable-users'],
    queryFn: assignableUsersApi.list,
  })

  const filters = useMemo(() => {
    const status: TicketStatus[] = includeClosed
      ? ['open', 'in_progress', 'resolved', 'closed']
      : ['open', 'in_progress']

    return {
      status,
      priority: priority || undefined,
      category: category || undefined,
      search: search.trim() || undefined,
      assigned_to: assignedMode === 'me'
        ? user?.id
        : assignedMode === 'specific'
          ? assignedTo || undefined
          : assignedMode === 'unassigned'
            ? 'unassigned'
            : undefined,
      queue,
      ticket_type_id: gapLink.ticketTypeId || undefined,
      subject: gapLink.subject || undefined,
      created_from: gapLink.createdFrom || undefined,
      created_to: gapLink.createdTo || undefined,
      per_page: 50,
    }
  }, [assignedMode, assignedTo, category, gapLink.createdFrom, gapLink.createdTo, gapLink.subject, gapLink.ticketTypeId, includeClosed, priority, queue, search, user?.id])

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', filters],
    queryFn: () => ticketApi.list(filters),
    enabled: assignedMode !== 'me' || Boolean(user?.id),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tickets"
        subtitle="Operational queue for merchant and lead inquiries."
        action={(
          <>
            <Link
              to="/tickets/stats"
              className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Ticket stats
            </Link>
            <Button onClick={() => setShowCreateModal(true)}>New ticket</Button>
          </>
        )}
      />

      {queue ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          <span>Showing {QUEUE_LABELS[queue]}. This is the same set as the accountability card.</span>
          <Link to="/tickets" className="font-medium text-amber-950 underline">Clear queue filter</Link>
        </div>
      ) : null}

      {gapLink.isGapLink ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          <span>
            Showing {data?.total ?? '…'} tickets for this knowledge gap. This is the same set as the Help hub row.
          </span>
          <Link to="/help/gaps" className="font-medium text-amber-950 underline">Back to knowledge gaps</Link>
        </div>
      ) : null}

      <SurfaceCard padding="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm font-medium text-gray-700">
              Search
              <input
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Subject, ticket #, MID, DBA"
              />
            </label>

            <label className="text-sm font-medium text-gray-700">
              Status
              <select
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={includeClosed ? 'all' : 'active'}
                onChange={(event) => setIncludeClosed(event.target.value === 'all')}
              >
                <option value="active">Open + in progress</option>
                <option value="all">All statuses</option>
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Priority
              <select
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={priority}
                onChange={(event) => setPriority(event.target.value as TicketPriority | '')}
              >
                <option value="">All</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Assigned to
              <select
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={assignedMode}
                onChange={(event) => setAssignedMode(event.target.value as 'me' | 'all' | 'specific' | 'unassigned')}
              >
                <option value="me">Me</option>
                <option value="all">All</option>
                <option value="unassigned">Unassigned</option>
                <option value="specific">Specific user</option>
              </select>
            </label>

            {assignedMode === 'specific' && (
              <label className="text-sm font-medium text-gray-700">
                User
                <select
                  className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={assignedTo}
                  onChange={(event) => setAssignedTo(event.target.value)}
                >
                  <option value="">Select user...</option>
                  {(users ?? []).map((assignableUser) => (
                    <option key={assignableUser.id} value={assignableUser.id}>
                      {assignableUser.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="text-sm font-medium text-gray-700">
              Category
              <select
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={category}
                onChange={(event) => setCategory(event.target.value as TicketCategory | '')}
              >
                <option value="">All</option>
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="onboarding">Onboarding</option>
                <option value="general">General</option>
              </select>
            </label>
          </div>
      </SurfaceCard>

      <SurfaceCard>
        <SectionCardTitle>Queue</SectionCardTitle>
        <div className="mt-4">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-gray-500" aria-live="polite">Loading tickets...</p>
          ) : data && data.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ticket</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Subject</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Merchant / Lead</th>
                    <th className="hidden px-3 py-2 text-left text-xs font-medium text-gray-500 xl:table-cell">MID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Assigned to</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Priority</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SLA</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="hidden px-3 py-2 text-left text-xs font-medium text-gray-500 xl:table-cell">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.data.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-blue-50/60">
                      <td className="px-3 py-2">
                        <Link to={`/tickets/${ticket.ticket_number}`} className="font-semibold text-blue-700 hover:text-blue-900">
                          {ticket.ticket_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/tickets/${ticket.ticket_number}`} className="font-semibold text-blue-700 hover:text-blue-900">
                          {ticket.subject}
                        </Link>
                        <div className="text-xs text-gray-500">{CATEGORY_LABELS[ticket.category]}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{recordName(ticket)}</td>
                      <td className="hidden px-3 py-2 text-gray-700 xl:table-cell">{ticket.merchant?.fiserv_mid ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {(ticket.assignees ?? []).length > 0
                          ? assigneeSummary(ticket)
                          : <span className="italic text-gray-500">Unassigned</span>}
                      </td>
                      <td className="px-3 py-2">
                        <PriorityPill priority={ticket.priority} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1">
                          <SlaPill ticket={ticket} />
                          <span className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-xs font-semibold ${slaTypeClass(ticket)}`}>
                            {slaTypeLabel(ticket)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={ticket.status} />
                      </td>
                      <td className="hidden px-3 py-2 text-gray-500 xl:table-cell">{formatAge(ticket.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">No tickets match the current filters.</p>
          )}
        </div>
      </SurfaceCard>

      {showCreateModal && (
        <CreateTicketModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  )
}

function TicketDetail({ id }: { id: string }) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { canAssignTickets, canResolveTickets, canCloseTickets, canCreateHelpArticles } = usePermissions()
  const [selectedAssignee, setSelectedAssignee] = useState('')
  const [resolutionNotes, setResolutionNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [commentBody, setCommentBody] = useState('')
  const [commentFiles, setCommentFiles] = useState<File[]>([])
  const [notifyMerchant, setNotifyMerchant] = useState(false)
  const [checklistForms, setChecklistForms] = useState<Record<string, { expanded: boolean; comment: string; file: File | null }>>({})

  const { data: users } = useQuery({
    queryKey: ['assignable-users'],
    queryFn: assignableUsersApi.list,
  })

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketApi.get(id),
  })

  const { data: comments, refetch: refetchComments } = useQuery({
    queryKey: ['ticket-comments', id],
    queryFn: () => ticketApi.listComments(id),
  })

  const refreshTicket = (updated: Ticket) => {
    queryClient.setQueryData(['ticket', id], updated)
    queryClient.invalidateQueries({ queryKey: ['tickets'] })
    setError(null)
  }

  const assignMutation = useMutation({
    mutationFn: (assignedTo: string) => ticketApi.addAssignee(id, assignedTo),
    onSuccess: (updated) => {
      setSelectedAssignee('')
      refreshTicket(updated)
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not assign ticket.'))
    },
  })

  const removeAssigneeMutation = useMutation({
    mutationFn: (userId: string) => ticketApi.removeAssignee(id, userId),
    onSuccess: refreshTicket,
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not remove assignee.'))
    },
  })

  const commentMutation = useMutation({
    mutationFn: () => ticketApi.postComment(id, {
      body: commentBody.trim(),
      notify_merchant: notifyMerchant,
      attachments: commentFiles,
    }),
    onSuccess: async () => {
      setCommentBody('')
      setCommentFiles([])
      setNotifyMerchant(false)
      await refetchComments()
      await queryClient.invalidateQueries({ queryKey: ['ticket', id] })
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not post comment.'))
    },
  })

  const statusMutation = useMutation({
    mutationFn: (payload: { status: TicketStatus; resolution_notes?: string }) => ticketApi.updateStatus(id, payload),
    onSuccess: (updated) => {
      refreshTicket(updated)
      setResolutionNotes('')
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not update ticket status.'))
    },
  })

  const completeChecklistMutation = useMutation({
    mutationFn: ({ item, comment, file }: { item: TicketChecklistItem; comment: string; file: File | null }) => (
      ticketApi.completeChecklistItem(id, item.id, {
        completion_comment: comment.trim() || null,
        completion_file: file,
      })
    ),
    onSuccess: (updated) => {
      refreshTicket(updated)
      setChecklistForms({})
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not complete checklist item.'))
    },
  })

  const uncompleteChecklistMutation = useMutation({
    mutationFn: (item: TicketChecklistItem) => ticketApi.uncompleteChecklistItem(id, item.id),
    onSuccess: refreshTicket,
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not reopen checklist item.'))
    },
  })

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-gray-500" aria-live="polite">Loading ticket...</p>
  }

  if (!ticket) {
    return <p className="py-8 text-center text-sm text-gray-500">Ticket not found.</p>
  }

  const recordLabel = recordName(ticket)
  const canWork = canResolveTickets()
  const canClose = canCloseTickets()
  const canAssign = canAssignTickets()

  const handleAssign = () => {
    if (!selectedAssignee || (ticket.assignees ?? []).some((assignee) => assignee.id === selectedAssignee)) return
    assignMutation.mutate(selectedAssignee)
  }

  const handleClaim = () => {
    if (!user?.id || (ticket.assignees ?? []).length > 0) return
    assignMutation.mutate(user.id)
  }

  const handleStatus = (status: TicketStatus) => {
    statusMutation.mutate({ status })
  }

  const handleResolve = () => {
    statusMutation.mutate({ status: 'resolved', resolution_notes: resolutionNotes.trim() })
  }

  const checklistItems = ticket.checklist_items ?? []
  const completedChecklistCount = checklistItems.filter((item) => item.completed_at).length
  const activityNames: Record<string, string> = {}
  for (const person of users ?? []) activityNames[person.id] = person.name
  for (const assignee of ticket.assignees ?? []) activityNames[assignee.id] = assignee.name
  if (ticket.creator) activityNames[ticket.creator.id] = ticket.creator.name
  for (const entry of ticket.events ?? []) {
    if (entry.actor) activityNames[entry.actor.id] = entry.actor.name
  }

  const expandChecklistItem = (item: TicketChecklistItem) => {
    setChecklistForms((current) => ({
      ...current,
      [item.id]: current[item.id] ?? { expanded: true, comment: '', file: null },
    }))
  }

  const handleCompleteChecklistItem = (item: TicketChecklistItem) => {
    const needsForm = item.comment_required || item.file_required
    const form = checklistForms[item.id]
    if (needsForm && !form?.expanded) {
      expandChecklistItem(item)
      return
    }
    completeChecklistMutation.mutate({
      item,
      comment: form?.comment ?? '',
      file: form?.file ?? null,
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={ticket.ticket_number}
        subtitle={ticket.subject}
        action={canWork ? (
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            Edit Ticket
          </Button>
        ) : undefined}
      />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/tickets" className="text-sm font-medium text-blue-700 hover:text-blue-900">Back to tickets</Link>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(ticket.ticket_number)}
            className="mt-2 block text-left text-sm font-semibold text-blue-700 hover:text-blue-900"
            title="Click to copy ticket number"
          >
            Copy ticket number
          </button>
          {ticket.merchant?.fiserv_mid && (
            <p className="mt-1 text-sm text-gray-500">MID {ticket.merchant.fiserv_mid}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill status={ticket.status} />
            <PriorityPill priority={ticket.priority} />
            <SlaPill ticket={ticket} />
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-semibold text-gray-600">
              {CATEGORY_LABELS[ticket.category]}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Description</CardTitle></CardHeader>
            <CardContent>
              {ticket.ticket_type && (
                <div className="mb-4 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2 text-sm text-blue-900">
                  <span className="font-semibold">{ticket.ticket_type.name}</span>
                  {ticket.ticket_type.additional_information && (
                    <p className="mt-1 whitespace-pre-wrap text-xs text-blue-800">{ticket.ticket_type.additional_information}</p>
                  )}
                </div>
              )}
              <p className="whitespace-pre-wrap text-sm text-gray-700">{ticket.body || 'No description provided.'}</p>
            </CardContent>
          </Card>

          <TicketHelpArticles
            ticketId={id}
            links={ticket.linked_articles ?? []}
            canEdit={canWork}
            onUpdated={refreshTicket}
          />

          <Card>
            <CardHeader><CardTitle>Activity</CardTitle></CardHeader>
            <CardContent>
              <TicketActivityTimeline
                events={ticket.events ?? []}
                names={activityNames}
                checklistItems={checklistItems}
              />
            </CardContent>
          </Card>

          {checklistItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Checklist</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-gray-700">
                    {completedChecklistCount} of {checklistItems.length} complete
                  </div>
                  <div className="h-2 min-w-[180px] flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{ width: `${Math.round((completedChecklistCount / checklistItems.length) * 100)}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {checklistItems.map((item) => {
                    const form = checklistForms[item.id] ?? { expanded: false, comment: '', file: null }
                    const isComplete = Boolean(item.completed_at)
                    return (
                      <div key={item.id} className="rounded-md border border-gray-200 p-3">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            aria-label={`Mark ${item.text} complete`}
                            className="mt-1"
                            checked={isComplete}
                            disabled={!canWork || completeChecklistMutation.isPending || uncompleteChecklistMutation.isPending}
                            onChange={(event) => {
                              if (event.target.checked) {
                                handleCompleteChecklistItem(item)
                              } else {
                                uncompleteChecklistMutation.mutate(item)
                              }
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium ${isComplete ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                              {item.text}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                              {item.comment_required && <span>Comment required</span>}
                              {item.file_required && <span>File required</span>}
                              {isComplete && (
                                <span>
                                  Completed by {item.completed_by?.name ?? 'Unknown'} {item.completed_at ? new Date(item.completed_at).toLocaleString() : ''}
                                </span>
                              )}
                              {item.completion_file_name && <span>File: {item.completion_file_name}</span>}
                            </div>
                            {item.completion_comment && (
                              <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-sm text-gray-700">{item.completion_comment}</p>
                            )}

                            {canWork && !isComplete && form.expanded && (
                              <div className="mt-3 space-y-3 rounded-md bg-gray-50 p-3">
                                <label className="block text-sm font-medium text-gray-700">
                                  Completion comment{item.comment_required ? ' *' : ''}
                                  <textarea
                                    className="mt-1 block min-h-[80px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                    value={form.comment}
                                    onChange={(event) => setChecklistForms((current) => ({
                                      ...current,
                                      [item.id]: { ...form, comment: event.target.value },
                                    }))}
                                  />
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                  Completion file{item.file_required ? ' *' : ''}
                                  <input
                                    type="file"
                                    className="mt-1 block text-sm"
                                    onChange={(event) => setChecklistForms((current) => ({
                                      ...current,
                                      [item.id]: { ...form, file: event.target.files?.[0] ?? null },
                                    }))}
                                  />
                                </label>
                                <Button
                                  onClick={() => handleCompleteChecklistItem(item)}
                                  disabled={completeChecklistMutation.isPending
                                    || (item.comment_required && form.comment.trim().length === 0)
                                    || (item.file_required && !form.file)}
                                >
                                  Complete Item
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Comments</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(comments ?? []).length === 0 ? (
                <p className="text-sm text-gray-500">No comments yet.</p>
              ) : (
                <div className="space-y-4">
                  {(comments ?? []).map((comment: TicketComment) => (
                    <div key={comment.id} className="flex gap-3">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${comment.source === 'system' ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700'}`}>
                        {comment.source === 'system' ? 'S' : (comment.user?.name?.[0] ?? '?')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          <span className="font-semibold text-gray-800">{comment.source === 'system' ? 'System' : comment.user?.name ?? 'Unknown'}</span>
                          {comment.user?.role && <span className="capitalize">{comment.user.role.replace('_', ' ')}</span>}
                          <span title={new Date(comment.created_at).toLocaleString()}>{formatAge(comment.created_at)} ago</span>
                          {!comment.is_internal && (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">Sent to merchant</span>
                          )}
                          {comment.email_sent_at && <span>Emailed {new Date(comment.email_sent_at).toLocaleString()}</span>}
                        </div>
                        <p className={`mt-1 whitespace-pre-wrap text-sm ${comment.source === 'system' ? 'italic text-gray-500' : 'text-gray-700'}`}>
                          {comment.body}
                        </p>
                        {comment.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {comment.attachments.map((attachment) => (
                              <a
                                key={attachment.id}
                                href={attachment.download_url ?? '#'}
                                className="rounded-md border border-gray-200 px-2 py-1 text-xs text-blue-700 hover:border-blue-300"
                              >
                                {attachment.file_name} ({formatBytes(attachment.file_size)})
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {canWork && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-700">
                    Comment
                    <textarea
                      className="mt-1 block min-h-[110px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={commentBody}
                      onChange={(event) => setCommentBody(event.target.value)}
                      placeholder="Post an update for the team."
                    />
                  </label>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <input
                      type="file"
                      multiple
                      aria-label="Attach files to comment"
                      onChange={(event) => setCommentFiles(Array.from(event.target.files ?? []).slice(0, 5))}
                      className="text-sm"
                    />
                    {commentFiles.length > 0 && (
                      <span className="text-xs text-gray-500">{commentFiles.length} file(s) selected</span>
                    )}
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-sm text-gray-700" title={!ticket.merchant_email ? 'No business email on file' : undefined}>
                    <input
                      type="checkbox"
                      checked={notifyMerchant}
                      disabled={!ticket.merchant_email}
                      onChange={(event) => setNotifyMerchant(event.target.checked)}
                    />
                    Notify merchant by email
                  </label>
                  {notifyMerchant && ticket.merchant_email && (
                    <p className="mt-1 text-xs text-gray-500">This comment will be sent to {ticket.merchant_email}.</p>
                  )}
                  <Button
                    className="mt-3"
                    onClick={() => commentMutation.mutate()}
                    disabled={commentMutation.isPending || commentBody.trim().length === 0}
                  >
                    {commentMutation.isPending ? 'Posting...' : 'Post comment'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {(ticket.status === 'resolved' || ticket.status === 'closed') && (
            <Card>
              <CardHeader><CardTitle>Resolution</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <p className="whitespace-pre-wrap text-sm text-gray-700">{ticket.resolution_notes || '-'}</p>
                <p className="text-xs text-gray-500">
                  Resolved {ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleString() : '-'}
                </p>
                <DraftHelpArticleButton
                  ticketId={ticket.id}
                  status={ticket.status}
                  canCreate={canCreateHelpArticles()}
                  configured={ticket.help_article_draft_configured === true}
                />
              </CardContent>
            </Card>
          )}

          {canWork && ticket.status === 'in_progress' && (
            <Card>
              <CardHeader><CardTitle>Resolve Ticket</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <label className="text-sm font-medium text-gray-700">
                  Resolution notes
                  <textarea
                    className="mt-1 block min-h-[110px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={resolutionNotes}
                    onChange={(event) => setResolutionNotes(event.target.value)}
                    placeholder="Summarize the fix or outcome."
                  />
                </label>
                <Button
                  onClick={handleResolve}
                  disabled={statusMutation.isPending || resolutionNotes.trim().length === 0}
                >
                  Resolve
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Record</div>
                <div className="font-medium text-gray-900">{recordLabel}</div>
                {ticket.merchant && (
                  <Link to={`/merchants?merchant=${ticket.merchant.id}`} className="text-xs text-blue-700 hover:text-blue-900">
                    View merchant
                  </Link>
                )}
                {ticket.lead && (
                  <Link to={`/leads/${ticket.lead.id}`} className="text-xs text-blue-700 hover:text-blue-900">
                    View lead
                  </Link>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned to</div>
                <div className="font-medium text-gray-900">{assigneeSummary(ticket)}</div>
                {canWork && (ticket.assignees ?? []).length === 0 && user?.id && (
                  <Button
                    className="mt-2"
                    variant="secondary"
                    onClick={handleClaim}
                    disabled={assignMutation.isPending}
                  >
                    Claim this ticket
                  </Button>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">SLA</div>
                <div className="mt-1 flex items-center gap-2">
                  <SlaPill ticket={ticket} />
                  <span className="text-gray-600">{ticket.sla_target ?? '-'}</span>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Due {ticket.sla_due_at ? new Date(ticket.sla_due_at).toLocaleString() : '-'}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Created by</div>
                <div>{ticket.creator?.name ?? '-'}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Created</div>
                <div>{new Date(ticket.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">First response</div>
                <div>{ticket.first_response_at ? new Date(ticket.first_response_at).toLocaleString() : 'Not yet'}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Source</div>
                <div className="capitalize">{ticket.source_channel ?? '-'}</div>
              </div>
            </CardContent>
          </Card>

          {canAssign && (
            <Card>
              <CardHeader><CardTitle>Assignment</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {(ticket.assignees ?? []).length === 0 ? (
                    <span className="text-sm italic text-gray-500">Unassigned</span>
                  ) : (
                    (ticket.assignees ?? []).map((assignee) => (
                      <span key={assignee.id} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-700">
                        {assignee.name}
                        <button
                          type="button"
                          className="text-gray-400 hover:text-rose-600"
                          onClick={() => removeAssigneeMutation.mutate(assignee.id)}
                          disabled={removeAssigneeMutation.isPending}
                          aria-label={`Remove ${assignee.name}`}
                        >
                          x
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <select
                  aria-label="Select teammate to assign"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={selectedAssignee}
                  onChange={(event) => setSelectedAssignee(event.target.value)}
                >
                  <option value="">Select teammate...</option>
                  {(users ?? []).map((assignableUser) => (
                    <option key={assignableUser.id} value={assignableUser.id}>
                      {assignableUser.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  onClick={handleAssign}
                  disabled={assignMutation.isPending || !selectedAssignee || (ticket.assignees ?? []).some((assignee) => assignee.id === selectedAssignee)}
                >
                  + Add assignee
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Workflow</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {canWork && ticket.status === 'open' && (
                <Button className="w-full" onClick={() => handleStatus('in_progress')} disabled={statusMutation.isPending}>
                  Start working
                </Button>
              )}
              {canWork && (ticket.status === 'resolved' || ticket.status === 'closed') && (
                <Button className="w-full" onClick={() => handleStatus('in_progress')} disabled={statusMutation.isPending}>
                  Reopen
                </Button>
              )}
              {canClose && ticket.status === 'resolved' && (
                <Button className="w-full" variant="secondary" onClick={() => handleStatus('closed')} disabled={statusMutation.isPending}>
                  Close
                </Button>
              )}
              {!canWork && !canClose && (
                <p className="text-sm text-gray-500">You do not have ticket workflow permissions.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {showEditModal && (
        <EditTicketModal
          ticket={ticket}
          onClose={() => setShowEditModal(false)}
          onUpdated={refreshTicket}
        />
      )}
    </div>
  )
}
